'use client';

import { type ReactNode, useEffect, useRef } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { useCreation } from '@ps/functions/hooks/useCreation';
import type { CreatorInfo } from '@ps/types/creator';
import { BehaviorSubject, Subject } from 'rxjs';

import type { AnyVideo } from '../../functions';
import {
  type AudioContextValue,
  AudioMuteContext,
  VideoClipsContext,
  type VideoClipsContextValue,
} from './context';

type VideoClipsProviderProps = {
  initialIndex?: number;
  /** The player's bio context — its clips' creator, for tagged-product URLs. */
  bioContext: BioContext;
  /**
   * The owning creator, for feeds whose clips carry no per-clip `author` (the
   * welcome video). `VideoFooter` renders its name when no author is present.
   */
  creatorInfo?: CreatorInfo;
  autoAdvance?: boolean;
  editable?: boolean;
  /**
   * Whether the in-player Share action can build a URL on this host. Pass
   * `false` where no clip permalink is available (the standalone `/videos`
   * library player) to hide the "Share" menu item. Defaults to shareable.
   */
  canShare?: boolean;
  children: ReactNode;
  onUpdated?: (video: AnyVideo) => void;
  onDeleted?: (video: AnyVideo) => void;
  onPrev?: () => boolean;
  onNext?: () => boolean;
  /**
   * Action dispatch — the host receives intents emitted by the
   * player (`openProfile`, `open-link`, `requireLogin`,
   * `lockedVideoTapped`, `INDEX_CHANGED`, `deleteVideo`, …).
   * Optional; when omitted, those intents no-op.
   */
  onAction?: (data: Record<string, unknown>) => void;
};

export function VideoClipsProvider({
  initialIndex = 0,
  bioContext,
  creatorInfo,
  autoAdvance,
  editable,
  canShare,
  children,
  onUpdated,
  onDeleted,
  onPrev,
  onNext,
  onAction,
}: VideoClipsProviderProps) {
  const paused$ = useCreation(() => new BehaviorSubject(false), []);
  const isOverlayHidden$ = useCreation(() => new BehaviorSubject(false), []);
  const currentIndex$ = useCreation(
    () => new BehaviorSubject(initialIndex),
    [initialIndex],
  );
  const indexUpdated$ = useCreation(() => new Subject<number>(), []);

  // ── Page size: prefer container dimensions, fall back to window ─────
  const pageSize$ = useCreation(
    () =>
      new BehaviorSubject({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
      }),
    [],
  );
  const [pageSize, setPageSize] = useBehaviorSubject(pageSize$);
  const containerRef = useRef<HTMLDivElement>(null);
  const usingContainerRef = useRef(false);

  // Container measurement via ResizeObserver — takes priority.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function measure() {
      const rect = el!.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        usingContainerRef.current = true;
        setPageSize({ width: rect.width, height: rect.height });
      }
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      usingContainerRef.current = false;
    };
  }, [setPageSize]);

  // Fallback: window resize — only used when container is not measured
  // (e.g. the standalone /video-player page where the container IS the viewport).
  const prevWindowSize = useRef({ width: 0, height: 0 });
  useEffect(() => {
    function handleResize() {
      if (usingContainerRef.current) return;
      const newSize = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      if (
        newSize.width !== prevWindowSize.current.width ||
        newSize.height !== prevWindowSize.current.height
      ) {
        prevWindowSize.current = newSize;
        setPageSize(newSize);
      }
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [setPageSize]);

  const isMobileLayout = pageSize.width <= 600;

  // Reset pause/overlay on index change
  useEffect(() => {
    const sub = currentIndex$.subscribe(() => {
      if (paused$.getValue()) paused$.next(false);
      if (isOverlayHidden$.getValue()) isOverlayHidden$.next(false);
    });
    return () => sub.unsubscribe();
  }, [currentIndex$, paused$, isOverlayHidden$]);

  const value: VideoClipsContextValue = {
    initialIndex,
    currentIndex$,
    indexUpdated$,
    pageSize,
    paused$,
    isOverlayHidden$,
    isMobileLayout,
    bioContext,
    creatorInfo,
    autoAdvance,
    editable,
    canShare,
    onUpdated,
    onDeleted,
    onPrev,
    onNext,
    onAction,
  };

  return (
    <AudioMuteProvider>
      <VideoClipsContext.Provider value={value}>
        <div ref={containerRef} className="flex h-full w-full flex-col">
          {children}
        </div>
      </VideoClipsContext.Provider>
    </AudioMuteProvider>
  );
}

function AudioMuteProvider({ children }: { children: ReactNode }) {
  const contextValueRef = useRef<AudioContextValue | null>(null);

  if (!contextValueRef.current) {
    contextValueRef.current = {
      muted$: new BehaviorSubject<boolean>(true), // Start muted for autoplay
    };
  }

  return (
    <AudioMuteContext.Provider value={contextValueRef.current}>
      {children}
    </AudioMuteContext.Provider>
  );
}
